/**
 * Payments/products API client — talks to the same-origin payment endpoints. Public catalog
 * reads need no auth; checkout + admin CRUD carry the authenticated headers (Clerk Bearer
 * token) from the save store, and the SERVER gates admin routes to allowlisted admins.
 *
 * Money is always in integer minor units (cents) end-to-end to avoid float bugs; format only
 * at the display edge (see formatPrice).
 */

import { authHeaders } from "./save";
import { track } from "./analytics";
import { PREMIUM_SEED_GRANT } from "./premiumSeeds";

/** What an item GRANTS in the game when purchased. "none" = no automatic effect. `refId`
 *  is the in-game asset id (e.g. a flower id) the entitlement unlocks for the buyer. */
export type GrantType = "none" | "plant" | "cosmetic" | "other";
export interface Grant { type: GrantType; refId: string; }

/** One admin-authored SECTION of a product's detail page. Either a bullet list ("What's Included") or a
 *  paragraph of text ("About" / "How it works"). Sections are ordered, add/delete/reorderable in the admin
 *  product editor and stored in `metadata.detailSections` (freeform JSONB — no migration). */
export interface DetailSection {
  id: string;
  heading: string;
  kind: "bullets" | "text";
  items?: string[];   // for kind "bullets"
  body?: string;      // for kind "text"
}

/**
 * Presentation extras stored in a product's `metadata` (JSON) — no DB migration needed. This is the agreed
 * convention for styling a product card on request (custom title font/colour, an explicit Petals price, …):
 * everything optional, safe defaults, so a product can be as plain or as bespoke as asked.
 */
export interface ProductMeta {
  images?: string[];     // gallery image URLs (first = primary/thumbnail); may be /api/media/:id uploads or external
  subtitle?: string;     // short tagline under the title
  badge?: string;        // corner ribbon, e.g. "Best value" / "New"
  features?: string[];   // "what you get" bullet points (shown ON the card)
  detailSections?: DetailSection[];   // ordered sections shown on the larger PRODUCT DETAIL page
  // --- per-product STYLE overrides (all optional) ---
  titleFont?: string;    // CSS font-family for the product title (e.g. "'Cinzel', serif")
  titleColor?: string;   // CSS colour for the product title (e.g. "#9a6cff")
  titleWeight?: number | string;  // CSS font-weight for the title
  accent?: string;       // accent colour used for price / highlights on this card
  petalsPrice?: number;  // explicit Petals price override (else derived from priceCents via the wheel economics)
}

/** Read the typed presentation fields out of a product's metadata (safe defaults). */
export function productMeta(p: { metadata?: Record<string, unknown> }): Required<ProductMeta> {
  const m = (p.metadata ?? {}) as Record<string, unknown>;
  const str = (k: string) => (typeof m[k] === "string" ? (m[k] as string) : "");
  return {
    images: Array.isArray(m.images) ? (m.images as unknown[]).filter((x): x is string => typeof x === "string") : [],
    subtitle: str("subtitle"),
    badge: str("badge"),
    features: Array.isArray(m.features) ? (m.features as unknown[]).filter((x): x is string => typeof x === "string") : [],
    detailSections: coerceDetailSections(m.detailSections),
    titleFont: str("titleFont"),
    titleColor: str("titleColor"),
    titleWeight: (typeof m.titleWeight === "number" || typeof m.titleWeight === "string") ? (m.titleWeight as number | string) : "",
    accent: str("accent"),
    petalsPrice: typeof m.petalsPrice === "number" ? (m.petalsPrice as number) : 0,
  };
}

/** Defensively parse `metadata.detailSections` into a clean, length-capped list (drops malformed entries). */
export function coerceDetailSections(raw: unknown): DetailSection[] {
  if (!Array.isArray(raw)) return [];
  const out: DetailSection[] = [];
  for (const s of raw as unknown[]) {
    if (!s || typeof s !== "object") continue;
    const o = s as Record<string, unknown>;
    const kind = o.kind === "text" ? "text" : "bullets";
    const heading = typeof o.heading === "string" ? o.heading.slice(0, 120) : "";
    const id = typeof o.id === "string" && o.id ? o.id : `s${out.length}`;
    if (kind === "bullets") {
      const items = Array.isArray(o.items) ? (o.items as unknown[]).filter((x): x is string => typeof x === "string").map((x) => x.slice(0, 200)).slice(0, 30) : [];
      out.push({ id, heading, kind, items });
    } else {
      out.push({ id, heading, kind, body: typeof o.body === "string" ? o.body.slice(0, 2000) : "" });
    }
    if (out.length >= 20) break;   // cap total sections
  }
  return out;
}

/** The detail-page sections to SHOW for a product: the admin-authored `detailSections` if any, else a
 *  synthesized default. For a single PLANT grant with no authored sections, the default is one "What's
 *  Included" bullet — "Ability to buy <Name> Seeds in-game" (derived at render time, never stored, so it
 *  stays overridable). `plantName` is passed in by the caller (resolved via flowers) to avoid a sim import here. */
export function productDetailSections(
  p: { grant?: Grant; metadata?: Record<string, unknown> },
  plantName?: string,
): DetailSection[] {
  const authored = coerceDetailSections((p.metadata ?? {}).detailSections);
  if (authored.length > 0) return authored;
  if (p.grant?.type === "plant") {
    const nm = plantName || "these seeds";
    return [{ id: "included", heading: "What's included", kind: "bullets", items: [`Ability to buy ${nm} Seeds in-game.`] }];
  }
  return [];
}

/**
 * WHAT A PRODUCT SAYS, in both shops (the site's ProductCard / ProductDetailModal and the game's
 * PremiumShopModal) — one place, so the two can never word a product differently.
 *
 * A PLANT reads as exactly two bullets and nothing else: the seeds it comes with and the shop unlock. That is
 * the whole of what buying it does (sim/premiumSeeds.ts), said plainly rather than pitched — so a plant ignores
 * the admin's description, feature list and detail sections. Anything else (a Petal pack, a perk) shows the
 * admin's own copy unchanged. `plantName` comes from the caller (resolved via flowers), as for
 * productDetailSections.
 */
export function productCopy(
  p: { description: string; grant?: Grant; metadata?: Record<string, unknown> },
  plantName: string | undefined,
): { description: string | null; features: string[]; sections: DetailSection[] } {
  if (p.grant?.type === "plant" && plantName) {
    return {
      description: null,
      features: [`${PREMIUM_SEED_GRANT}× ${plantName} Seeds`, `Buy more ${plantName} Seeds in the in-game shop`],
      sections: [],
    };
  }
  return { description: p.description || null, features: productMeta(p).features, sections: productDetailSections(p, plantName) };
}

export interface Product {
  id: number;
  name: string;
  description: string;
  priceCents: number;
  currency: string;
  active: boolean;
  sortOrder: number;
  grant: Grant;
  metadata: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

/** A product's editable fields (create/update payload). */
export interface ProductInput {
  name: string;
  description: string;
  priceCents: number;
  currency: string;
  active: boolean;
  sortOrder: number;
  grant: Grant;
  metadata?: Record<string, unknown>;
}

/** Format integer cents as a currency string, e.g. 499 → "$4.99". */
export function formatPrice(cents: number, currency = "usd"): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

// ---- public catalog --------------------------------------------------------

/** The active products for the Shop (+ whether payments are actually enabled server-side, and whether purchases are
 *  PAUSED — Kickstarter mode closes the Premium Shop until launch; see SHOP_PAUSED_TEXT). */
export async function listProducts(): Promise<{ products: Product[]; paymentsEnabled: boolean; purchasesPaused: boolean }> {
  const res = await fetch("/api/products");
  if (!res.ok) throw new Error(`products ${res.status}`);
  const j = await res.json();
  return { products: j.products ?? [], paymentsEnabled: !!j.paymentsEnabled, purchasesPaused: !!j.purchasesPaused };
}

/** What every shop says while purchases are paused (Kickstarter mode). */
export const SHOP_PAUSED_TEXT = "The Premium Shop opens with the full game. Purchases are paused during the Kickstarter demo.";

// ---- checkout --------------------------------------------------------------

/** Start a Stripe Checkout for a product; returns the redirect URL to send the user to. */
export async function startCheckout(productId: number): Promise<string> {
  track("checkout_start", { product: String(productId), kind: "item" });   // the end is the server's revenue_events
  const res = await fetch("/api/checkout", {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ productId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.url) throw new Error(data.error || `checkout ${res.status}`);
  return data.url as string;
}

// ---- buy with Petals -------------------------------------------------------

/**
 * The channel a Petals purchase is announced on, so an open GAME tab can take the spend off its live balance.
 * The shop is its own route: the game is not mounted beside it, so the only way to reach a running game is
 * another tab. The message carries the server's running debit total, and the game applies whatever it has
 * not yet applied (App.tsx) — a missed or repeated message can therefore neither skip nor double a debit.
 */
export const PETALS_SPENT_CHANNEL = "shelf-garden:petals-spent";
export interface PetalsSpentMessage { petalsDebited: number; }

/** The signed-in player's Petal balance on their cloud save. Throws when signed out (401). */
export async function myPetals(): Promise<number> {
  const res = await fetch("/api/me/petals", { headers: await authHeaders() });
  if (!res.ok) throw new Error(`petals ${res.status}`);
  return (await res.json()).petals as number;
}

export interface PetalsPurchase {
  alreadyOwned: boolean;
  price: number;
  spent: number;
  /** the balance AFTER the purchase, read off the server's save */
  petals: number;
  /** the server's running total of Petal debits — see PETALS_SPENT_CHANNEL */
  petalsDebited: number;
}

/**
 * Buy a product with Petals. The SERVER debits the balance on the cloud save and grants the entitlement;
 * nothing here touches the balance. A successful spend is announced on PETALS_SPENT_CHANNEL so a running
 * game updates its own balance through `setPetals`. A short balance throws with the shortfall in the message.
 */
export async function buyWithPetals(productId: number): Promise<PetalsPurchase> {
  track("checkout_start", { product: String(productId), kind: "petals" });  // the end is the server's economy_events
  const res = await fetch("/api/shop/buy-with-petals", {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ productId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 402 && typeof data.shortfall === "number") {
      throw new Error(`Not enough Petals. You need ${data.shortfall.toLocaleString()} more.`);
    }
    throw new Error(data.error || `purchase ${res.status}`);
  }
  if (data.spent > 0 && typeof BroadcastChannel !== "undefined") {
    const ch = new BroadcastChannel(PETALS_SPENT_CHANNEL);
    ch.postMessage({ petalsDebited: data.petalsDebited } satisfies PetalsSpentMessage);
    ch.close();
  }
  return data as PetalsPurchase;
}

/** One card purchase as the player's own history lists it (`status`: pending · paid · failed · expired · refunded). */
export interface MyPurchase { id: number; productId: number | null; name: string | null; amountCents: number; currency: string; status: string; createdAt: string; paidAt: string | null }

/** The signed-in user's entitlements + purchase history (the Profile page's "Your purchases"). */
export async function myPurchases(): Promise<{
  entitlements: Array<{ productId: number | null; name: string | null; grant: Grant; grantedAt: string }>;
  purchases: MyPurchase[];
}> {
  const res = await fetch("/api/me/purchases", { headers: await authHeaders() });
  if (!res.ok) throw new Error(`purchases ${res.status}`);
  return res.json();
}

/** GAME-facing: the resolved asset ids the signed-in user OWNS via purchases (shop-only
 *  unlocks). The game reads this to unlock granted plants/cosmetics for the player. */
export async function myEntitlements(): Promise<{ plants: string[]; cosmetics: string[]; other: string[] }> {
  const res = await fetch("/api/me/entitlements", { headers: await authHeaders() });
  if (!res.ok) throw new Error(`entitlements ${res.status}`);
  return res.json();
}

// ---- admin media upload ----------------------------------------------------

/** Upload an image file (admin). POSTs the raw bytes; the server stores it in Postgres and returns a
 *  `/api/media/:id` URL to drop into a product gallery or an image block. Enforces a 3 MB cap client-side too. */
export async function uploadMedia(file: File, alt = ""): Promise<{ id: number; url: string }> {
  if (file.size > 3 * 1024 * 1024) throw new Error("Image too large (max 3 MB).");
  const headers = await authHeaders({ "content-type": file.type || "application/octet-stream" });
  const res = await fetch(`/api/admin/media${alt ? `?alt=${encodeURIComponent(alt)}` : ""}`, {
    method: "POST", headers, body: file,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `upload ${res.status}`);
  return data as { id: number; url: string };
}

// ---- admin product CRUD ----------------------------------------------------

export async function adminListProducts(): Promise<Product[]> {
  const res = await fetch("/api/admin/products", { headers: await authHeaders() });
  if (!res.ok) throw new Error(`admin products ${res.status}`);
  return (await res.json()).products;
}

export async function adminCreateProduct(input: ProductInput): Promise<Product> {
  const res = await fetch("/api/admin/products", {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(input),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `create ${res.status}`);
  return data.product;
}

export async function adminUpdateProduct(id: number, input: ProductInput): Promise<Product> {
  const res = await fetch(`/api/admin/products/${id}`, {
    method: "PUT",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(input),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `update ${res.status}`);
  return data.product;
}

export async function adminDeleteProduct(id: number): Promise<void> {
  const res = await fetch(`/api/admin/products/${id}`, { method: "DELETE", headers: await authHeaders() });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `delete ${res.status}`); }
}
